import { dataLayerConfig, resolvePhoneNumberFormat, TAGLink, TAGSvgIcon } from '@aspendental/shared-components-web';
import { Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { addToCalendar } from './Confirmation.helpers';
import ConfirmationCard from './ConfirmationCard';
import MarketingCard from './MarketingCard/MarketingCard';

import { useAppStore, usePatientInformationStore } from '@src/store';
import useAppointmentInformationStore from '@src/store/appointment-information.store';
import DataLayerIntegrityCheck from '@src/utils/datalayer-integrity-check';
import { getPhoneNumber } from '@src/utils/get-phone-number';

export default function Confirmation({ enablePtcSlotFilter }: Readonly<{ enablePtcSlotFilter?: boolean }>) {
	const router = useRouter();
	const { t } = useTranslation('index');
	const {
		chosenAppointmentData,
		chosenCenterData,
		presetCenterID,
		presetCenterName,
		trackAppointmentDaysShown,
		trackAppointmentTimesShown,
	} = useAppointmentInformationStore();
	const { salesforceGuid, salesforceAccountId, leadSegment20, salesforceS20, salesforceTier } =
		usePatientInformationStore((store) => store);
	const { successCards } = useAppStore((store) => store);
	const phoneNumber = getPhoneNumber(chosenCenterData);
	const address = `${chosenCenterData?.address?.address1 ?? ''} ${chosenCenterData?.address?.address2 ?? ''}`.trim();
	const city = chosenCenterData?.address?.city;
	const state = chosenCenterData?.address?.stateCode;
	const zipCode = chosenCenterData?.address?.zipCode;
	const appointmentDateTime = chosenAppointmentData?.timeSlot.datetime.replace(' ', 'T');
	const appointmentTime = chosenAppointmentData?.timeSlot.time;
	const location = `${address}, ${city}, ${state} ${zipCode}`;

	const handleAddToCalendar = () => {
		dataLayerConfig({ event: 'scheduleform_step5_addtocalendar' });
		addToCalendar(appointmentDateTime, location);
	};

	const handleGetDirections = () => {
		dataLayerConfig({ event: 'scheduleform_step5_getdirections' });
		window.open('https://www.google.com/maps/search/?api=1&query=Clear Choice', '_blank', 'noopener,noreferrer');
	};

	const appointmentDetails = [
		{
			icon: <TAGSvgIcon dataTestId="icon_calendar" icon="CalendarDateTime" />,
			content: [
				new Intl.DateTimeFormat(router.locale, { dateStyle: 'full' }).format(
					new Date(appointmentDateTime ?? new Date())
				),
				appointmentTime,
				<Button key="addToCalendar" variant="tertiaryDefault" size="S" onClick={() => handleAddToCalendar()}>
					{t('addToCalendar')}
				</Button>,
			],
		},
		{
			icon: <TAGSvgIcon icon="CCOffice" />,
			content: [
				address,
				`${city}, ${state} ${zipCode}`,
				<Typography key="phoneNumber" variant="bodyLargeBook">
					<TAGLink
						href={`tel:+1${phoneNumber}`}
						linkSx={{ textDecoration: 'none', color: 'text.interactive' }}
					>
						{resolvePhoneNumberFormat(phoneNumber ?? '')}
					</TAGLink>
				</Typography>,
				<Button size="S" variant="tertiaryDefault" key="directions" onClick={() => handleGetDirections()}>
					{t('getDirections')}
				</Button>,
			],
		},
	];

	useEffect(() => {
		dataLayerConfig({
			event: 'appointment_step',
			step_name: 'complete',
			lead_id: salesforceGuid,
			lead_tier: salesforceS20 && salesforceS20 != 21 ? salesforceTier : 'T1',
			lead_segment: salesforceS20 ?? '21',
			website_selected_center_name: presetCenterName,
			website_selected_center_id: presetCenterID,
			appointment_selected_center_name: chosenCenterData?.name,
			appointment_selected_center_id: chosenCenterData?.code,
			ptc: enablePtcSlotFilter ? '1' : '0',
			appointment_time_slots: trackAppointmentTimesShown,
			appointment_days_shown: trackAppointmentDaysShown,
		});

		DataLayerIntegrityCheck('complete');

		dataLayerConfig({
			event: 'scheduleform_step5',
			lead_segment20: leadSegment20,
			salesforce_guid: salesforceGuid,
			salesforce_account_id: salesforceAccountId,
		});
	}, [salesforceGuid, salesforceAccountId, leadSegment20]);

	return (
		<>
			<Grid container spacing={{ md: 2, xs: 1 }} my={{ md: 2, xs: 0 }} mb={{ md: 0, xs: 2 }}>
				{appointmentDetails.map(({ icon, content }, index) => {
					return (
						<Grid item key={index} xs={12} sm={6}>
							<ConfirmationCard icon={icon} content={content} />
						</Grid>
					);
				})}
			</Grid>

			<Grid container spacing={2} mt={2}>
				{successCards?.map((card) => {
					if (card) {
						return <MarketingCard card={card} key={card?.sys.id} />;
					}
				})}
			</Grid>
		</>
	);
}
